import { useState, useEffect } from 'react';
import axios from 'axios';
import { ChevronLeft, ChevronRight, CheckCircle, AlertTriangle, Clock } from 'lucide-react';
import { Button } from './ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SEDI = [
  { code: 'valeggio', name: 'Valeggio sul Mincio', cls: 'bg-[#283880] text-white', dot: 'bg-[#283880]' },
  { code: 'verona', name: 'Verona', cls: 'bg-[#C9A227] text-white', dot: 'bg-[#C9A227]' },
  { code: 'peschiera', name: 'Peschiera del Garda', cls: 'bg-teal-600 text-white', dot: 'bg-teal-600' },
  { code: '', name: 'Sede non indicata', cls: 'bg-slate-400 text-white', dot: 'bg-slate-400' },
];
const sedeOf = (a) => SEDI.find((s) => s.code === (a.sede?.code || '')) || SEDI[3];
const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const monday = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); x.setDate(x.getDate() - ((x.getDay() + 6) % 7)); return x; };
const romeDay = (iso) => new Date(iso).toLocaleDateString('en-CA', { timeZone: 'Europe/Rome' });
const romeTime = (iso) => new Date(iso).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Rome' });

const ResponseIcon = ({ r }) => {
  if (r === 'confermato') return <CheckCircle className="w-3.5 h-3.5 text-green-300" title="Confermato" />;
  if (r === 'da_spostare') return <AlertTriangle className="w-3.5 h-3.5 text-amber-200" title="Da spostare" />;
  return <Clock className="w-3.5 h-3.5 opacity-60" title="In attesa di risposta" />;
};

const ApptChip = ({ a }) => {
  const s = sedeOf(a);
  return (
    <div className={`${s.cls} rounded-md p-2 text-xs space-y-0.5 ${a.response === 'da_spostare' ? 'ring-2 ring-amber-400' : ''}`} data-testid={`agenda-appt-${a.pratica_id}`}>
      <p className="flex items-center justify-between font-bold">{romeTime(a.appuntamento)}<ResponseIcon r={a.response} /></p>
      <p className="font-medium truncate">{a.client_name}</p>
      <p className="opacity-80 truncate">{a.titolo}</p>
      {a.response === 'da_spostare' && a.note && <p className="bg-white/20 rounded px-1 py-0.5 italic" data-testid={`agenda-appt-note-${a.pratica_id}`}>“{a.note}”</p>}
    </div>
  );
};

export const AdminAgendaTab = () => {
  const [start, setStart] = useState(monday(new Date()));
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState('tutte');

  useEffect(() => {
    axios.get(`${API}/admin/agenda`, { params: { start: ymd(start) }, headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
      .then((r) => setItems(r.data)).catch(() => setItems([]));
  }, [start]);

  const days = Array.from({ length: 7 }, (_, i) => { const d = new Date(start); d.setDate(d.getDate() + i); return d; });
  const shift = (n) => { const d = new Date(start); d.setDate(d.getDate() + n * 7); setStart(d); };
  const visible = items.filter((a) => filter === 'tutte' || (a.sede?.code || '') === filter);
  const toMove = items.filter((a) => a.response === 'da_spostare').length;
  const today = ymd(new Date());

  return (
    <div className="space-y-6" data-testid="admin-agenda-tab">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-[#283880]">Agenda appuntamenti</h2>
          <p className="text-sm text-slate-500" data-testid="agenda-week-label">
            Settimana dal {days[0].toLocaleDateString('it-IT', { day: 'numeric', month: 'long' })} al {days[6].toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' })}
            {' · '}{items.length} appuntamenti{toMove > 0 && <span className="text-amber-600 font-medium"> · {toMove} da spostare</span>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => shift(-1)} data-testid="agenda-prev-week"><ChevronLeft className="w-4 h-4" /></Button>
          <Button variant="outline" size="sm" onClick={() => setStart(monday(new Date()))} data-testid="agenda-this-week">Questa settimana</Button>
          <Button variant="outline" size="sm" onClick={() => shift(1)} data-testid="agenda-next-week"><ChevronRight className="w-4 h-4" /></Button>
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        {[{ code: 'tutte', name: 'Tutte le sedi', dot: 'bg-gradient-to-r from-[#283880] to-[#C9A227]' }, ...SEDI.slice(0, 3)].map((s) => (
          <button key={s.code} onClick={() => setFilter(s.code)} data-testid={`agenda-filter-${s.code}`}
            className={`flex items-center gap-2 text-xs px-3 py-1.5 rounded-full border transition-colors ${filter === s.code ? 'border-[#283880] bg-[#283880]/5 text-[#283880] font-medium' : 'border-slate-200 text-slate-500 hover:border-slate-400'}`}>
            <span className={`w-2.5 h-2.5 rounded-full ${s.dot}`} />{s.name}
          </button>
        ))}
        <span className="flex items-center gap-3 text-xs text-slate-400 ml-auto">
          <span className="flex items-center gap-1"><CheckCircle className="w-3.5 h-3.5 text-green-600" />confermato</span>
          <span className="flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5 text-amber-500" />da spostare</span>
          <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" />in attesa</span>
        </span>
      </div>
      <div className="overflow-x-auto">
        <div className="grid grid-cols-7 gap-2 min-w-[1000px]">
          {days.map((d) => {
            const key = ymd(d);
            const list = visible.filter((a) => romeDay(a.appuntamento) === key);
            return (
              <div key={key} className={`bg-white rounded-xl shadow-sm p-3 min-h-[260px] ${key === today ? 'ring-2 ring-[#C9A227]' : ''}`} data-testid={`agenda-day-${key}`}>
                <p className="text-xs uppercase tracking-wider text-slate-400">{d.toLocaleDateString('it-IT', { weekday: 'short' })}</p>
                <p className="text-lg font-bold text-[#283880] mb-3">{d.getDate()}</p>
                <div className="space-y-2">
                  {list.length === 0 && <p className="text-xs text-slate-300">—</p>}
                  {list.map((a) => <ApptChip key={a.pratica_id} a={a} />)}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
